import { useEffect, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useToast } from "../../../hooks/use-toast";
import { useCreateTemplate, useUpdateTemplate } from "../hooks/use-template-queries";
import { AVAILABLE_SCREENS } from "../constants/screens";
import { OpsTemplate } from "../types";

interface TemplateFormDialogProps {
  template: OpsTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

export function TemplateFormDialog({
  template,
  isOpen,
  onClose,
}: TemplateFormDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [screens, setScreens] = useState<string[]>([]);
  const [nameError, setNameError] = useState("");
  const [apiError, setApiError] = useState("");

  const { toast } = useToast();
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setDescription("");
      setScreens([]);
      setNameError("");
      setApiError("");
      return;
    }

    if (template) {
      setName(template.name);
      setDescription(template.description || "");
      setScreens(template.screens);
      setNameError("");
      setApiError("");
    }
  }, [template, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    if (!name.trim()) {
      setNameError("Nome é obrigatório");
      return;
    }
    setNameError("");

    const screensArray = screens;

    try {
      if (template) {
        await updateMutation.mutateAsync({
          id: template.id,
          name: name.trim(),
          description: description.trim() || undefined,
          screens: screensArray,
          uploads: template.uploads,
        });
        toast({ title: "Template atualizado com sucesso" });
      } else {
        await createMutation.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          screens: screensArray,
          uploads: [],
        });
        toast({ title: "Template criado com sucesso" });
      }
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Erro ao salvar template";
      setApiError(msg);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {template ? "Editar Template" : "Adicionar Template"}
          </DialogTitle>
          <DialogDescription>
            {template
              ? "Atualize as informações do template"
              : "Adicione um novo template de operação"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {apiError && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-800">{apiError}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="template-name">
              Nome <span className="text-red-500">*</span>
            </Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex: Standard Rayo"
            />
            {nameError && <p className="text-sm text-red-600">{nameError}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-description">Descrição</Label>
            <Input
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição opcional do template"
            />
          </div>

          <div className="space-y-2">
            <Label>Telas</Label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_SCREENS.map((screen) => {
                const isSelected = screens.includes(screen.value);
                return (
                  <Badge
                    key={screen.value}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={screen.label}
                    tabIndex={0}
                    className="cursor-pointer select-none transition-all hover:opacity-80 text-sm px-3 py-1.5"
                    style={
                      isSelected
                        ? {
                            backgroundColor: "hsl(221, 83%, 53%)",
                            borderColor: "hsl(221, 83%, 53%)",
                            color: "#fff",
                          }
                        : {
                            backgroundColor: "transparent",
                            borderColor: "#94a3b8",
                            color: "#64748b",
                          }
                    }
                    onClick={() =>
                      setScreens(
                        isSelected
                          ? screens.filter((s) => s !== screen.value)
                          : [...screens, screen.value]
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        setScreens(
                          isSelected
                            ? screens.filter((s) => s !== screen.value)
                            : [...screens, screen.value]
                        );
                      }
                    }}
                  >
                    {screen.label}
                  </Badge>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {template ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
